import React, { useRef, useState, useCallback, Suspense, useMemo, Component } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Html } from '@react-three/drei';
import * as THREE from 'three';
import { VEHICLE_SPECS, CATEGORY_COLORS } from '../data/components';
import { SeatModel, ReclinerModel, SofaBedModel, FoldableSeatModel, PantryModel, FridgeModel, TrayTableModel, BBQModel, ToiletModel, TentModel, CanopyModel, UmbrellaModel, OutdoorSeatModel, CabinetModel, StorageModel, WardrobeModel, PartitionModel, SpeakerModel, TVModel, ControlPanelModel, ChargingDockModel, PlatformLiftModel, FallbackModel } from './ComponentModels';

// Error boundary for Html components to prevent React 19 reconciliation issues
class HtmlErrorBoundary extends Component {
  state = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error) { console.warn('Html component error:', error); }
  render() { return this.state.hasError ? null : this.props.children; }
}

// Safe Html wrapper - re-enabled after fixing visual-edits compatibility
function SafeHtml({ children, ...props }) {
  return (
    <HtmlErrorBoundary>
      <Html {...props}>{children}</Html>
    </HtmlErrorBoundary>
  );
}

const CELL = 0.15;
const COLS = VEHICLE_SPECS.gridCols;
const ROWS = VEHICLE_SPECS.gridRows;

const MODEL_MAP = {
  'seat-captain': SeatModel,
  'seat-recliner': ReclinerModel,
  'seat-sofa': SofaBedModel,
  'seat-foldable': FoldableSeatModel,
  'util-pantry': PantryModel,
  'util-fridge': FridgeModel,
  'util-tray': TrayTableModel,
  'util-bbq': BBQModel,
  'util-toilet': ToiletModel,
  'life-tent': TentModel,
  'life-canopy': CanopyModel,
  'life-umbrella': UmbrellaModel,
  'life-outdoor': OutdoorSeatModel,
  'store-overhead': CabinetModel,
  'store-underseat': StorageModel,
  'store-wardrobe': WardrobeModel,
  'store-partition': PartitionModel,
  'elec-sound': SpeakerModel,
  'elec-tv': TVModel,
  'elec-control': ControlPanelModel,
  'elec-charger': ChargingDockModel,
  'elec-platform': PlatformLiftModel,
};

function getModelForComp(compId) {
  for (const [prefix, Model] of Object.entries(MODEL_MAP)) {
    if (compId.startsWith(prefix)) return Model;
  }
  return FallbackModel;
}

function PlacedComponent3D({ pc, comp, isSelected, isHovered, onSelect, onHover, customMaterials }) {
  const meshRef = useRef();
  const colors = CATEGORY_COLORS[comp.category] || CATEGORY_COLORS.Storage;
  const x = (pc.x + comp.grid_w / 2) * CELL - (COLS * CELL) / 2;
  const z = (pc.y + comp.grid_h / 2) * CELL - (ROWS * CELL) / 2;
  const matProps = customMaterials?.[pc.instance_id] || {};
  const ModelComponent = getModelForComp(comp.comp_id);
  const rotation = pc.rotation || 0;
  const rotationRad = (rotation * Math.PI) / 180; // Convert degrees to radians

  useFrame((_, delta) => {
    if (meshRef.current) {
      const targetY = isSelected ? 0.02 : 0;
      meshRef.current.position.y += (targetY - meshRef.current.position.y) * 5 * delta;
    }
  });

  return (
    <group
      ref={meshRef}
      position={[x, 0, z]}
      rotation={[0, rotationRad, 0]}
      onClick={(e) => { e.stopPropagation(); onSelect(pc.instance_id); }}
      onPointerOver={(e) => { e.stopPropagation(); onHover(pc.instance_id); document.body.style.cursor = 'pointer'; }}
      onPointerOut={() => { onHover(null); document.body.style.cursor = 'default'; }}
    >
      <ModelComponent
        width={comp.grid_w}
        height={comp.grid_h}
        color={matProps.color || colors.fill}
        fabricColor={matProps.fabricColor}
        woodFinish={matProps.woodFinish}
      />

      {(isSelected || isHovered) && (
        <mesh position={[0, -0.005, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <planeGeometry args={[comp.grid_w * CELL + 0.02, comp.grid_h * CELL + 0.02]} />
          <meshBasicMaterial color={isSelected ? '#3b82f6' : '#60a5fa'} transparent opacity={isSelected ? 0.4 : 0.2} />
        </mesh>
      )}

      {(isSelected || isHovered) && (
        <SafeHtml key={`html-${pc.instance_id}`} position={[0, 0.35, 0]} center distanceFactor={3}>
          <div key={`label-${pc.instance_id}`} className="bg-slate-900/90 backdrop-blur px-2 py-1 rounded text-[10px] text-white whitespace-nowrap border border-white/10 shadow-xl pointer-events-none select-none">
            {comp.name}
          </div>
        </SafeHtml>
      )}
    </group>
  );
}

function GridClickPlane({ onGridClick, validPlacements, moveComp }) {
  const handlePointerDown = useCallback((e) => {
    const point = e.point;
    const gx = Math.floor((point.x + (COLS * CELL) / 2) / CELL);
    const gy = Math.floor((point.z + (ROWS * CELL) / 2) / CELL);
    if (gx >= 0 && gx < COLS && gy >= 0 && gy < ROWS) {
      onGridClick(gx, gy);
    }
  }, [onGridClick]);

  return (
    <group>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.002, 0]} onClick={handlePointerDown} visible={false}>
        <planeGeometry args={[COLS * CELL + 1, ROWS * CELL + 1]} />
        <meshBasicMaterial transparent opacity={0} />
      </mesh>

      {validPlacements && moveComp && validPlacements.map((vp, i) => (
        <mesh
          key={i}
          position={[
            (vp.x + moveComp.grid_w / 2) * CELL - (COLS * CELL) / 2,
            0.005,
            (vp.y + moveComp.grid_h / 2) * CELL - (ROWS * CELL) / 2
          ]}
          rotation={[-Math.PI / 2, 0, 0]}
          onClick={(e) => { e.stopPropagation(); onGridClick(vp.x, vp.y); }}
        >
          <planeGeometry args={[moveComp.grid_w * CELL - 0.01, moveComp.grid_h * CELL - 0.01]} />
          <meshBasicMaterial color="#10b981" transparent opacity={0.3} />
        </mesh>
      ))}
    </group>
  );
}

function VehicleFloor() {
  const w = COLS * CELL;
  const h = ROWS * CELL;
  const driverH = VEHICLE_SPECS.driverZone.h * CELL;

  return (
    <group>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.005, 0]} receiveShadow>
        <planeGeometry args={[w, h]} />
        <meshStandardMaterial color="#0a0f1e" roughness={0.9} />
      </mesh>

      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.002, -h / 2 + driverH / 2]}>
        <planeGeometry args={[w, driverH]} />
        <meshStandardMaterial color="#1e293b" transparent opacity={0.6} />
      </mesh>

      <SafeHtml key="driver-area-label" position={[0, 0.05, -h / 2 + driverH / 2]} center>
        <div key="driver-text" className="font-jetbrains text-[9px] text-slate-600 uppercase tracking-[0.2em] pointer-events-none select-none">Driver Area</div>
      </SafeHtml>
      <SafeHtml key="front-label" position={[0, 0.05, -h / 2 - 0.08]} center>
        <div key="front-text" className="font-jetbrains text-[8px] text-blue-500/50 uppercase tracking-[0.3em] pointer-events-none select-none">Front</div>
      </SafeHtml>
      <SafeHtml key="rear-label" position={[0, 0.05, h / 2 + 0.08]} center>
        <div key="rear-text" className="font-jetbrains text-[8px] text-blue-500/50 uppercase tracking-[0.3em] pointer-events-none select-none">Rear</div>
      </SafeHtml>

      {VEHICLE_SPECS.doorZones.map((dz, i) => (
        <mesh key={i} rotation={[-Math.PI / 2, 0, 0]}
          position={[(dz.x + dz.w / 2) * CELL - w / 2, 0.003, (dz.y + dz.h / 2) * CELL - h / 2]}>
          <planeGeometry args={[dz.w * CELL, dz.h * CELL]} />
          <meshStandardMaterial color="#f59e0b" transparent opacity={0.1} />
        </mesh>
      ))}

      {/* Grid lines using thin meshes */}
      {Array.from({ length: COLS + 1 }).map((_, i) => (
        <mesh key={`gv${i}`} position={[i * CELL - w / 2, 0.001, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <planeGeometry args={[0.001, h]} />
          <meshBasicMaterial color="#1e3a5f" transparent opacity={0.3} />
        </mesh>
      ))}
      {Array.from({ length: ROWS + 1 }).map((_, i) => (
        <mesh key={`gh${i}`} position={[0, 0.001, i * CELL - h / 2]} rotation={[-Math.PI / 2, 0, 0]}>
          <planeGeometry args={[w, 0.001]} />
          <meshBasicMaterial color="#1e3a5f" transparent opacity={0.3} />
        </mesh>
      ))}
    </group>
  );
}

export default function ThreeCanvas({
  components, placedComponents, onDrop, onMove, onRemove,
  selectedInstance, setSelectedInstance, moveMode, setMoveMode,
  validPlacements, customMaterials, viewMode = '3d'
}) {
  const [hoveredInstance, setHoveredInstance] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const containerRef = useRef(null);

  const moveComp = useMemo(() => {
    if (!moveMode) return null;
    const pc = placedComponents.find(p => p.instance_id === moveMode);
    if (!pc) return null;
    return components.find(c => c.comp_id === pc.comp_id);
  }, [moveMode, placedComponents, components]);

  const handleGridClick = useCallback((gx, gy) => {
    if (moveMode) {
      const isValid = validPlacements.some(vp => vp.x === gx && vp.y === gy);
      if (isValid) onMove(moveMode, gx, gy);
      setMoveMode(null);
      setSelectedInstance(null);
    } else {
      setSelectedInstance(null);
    }
  }, [moveMode, validPlacements, onMove, setMoveMode, setSelectedInstance]);

  // Handle HTML5 drag and drop
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDropEvent = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    try {
      const data = e.dataTransfer.getData('application/json');
      if (!data) return;
      
      const comp = JSON.parse(data);
      if (!comp || !comp.comp_id) return;
      
      // Calculate drop position on the grid
      const container = containerRef.current;
      if (!container) return;
      
      const rect = container.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      // Convert screen coords to grid coords (approximate center drop)
      // The grid is 12 cols x 20 rows
      const gridX = Math.floor((x / rect.width) * COLS);
      const gridY = Math.floor((y / rect.height) * ROWS);
      
      // Offset to center the component
      const dropX = Math.max(0, Math.min(gridX - Math.floor(comp.grid_w / 2), COLS - comp.grid_w));
      const dropY = Math.max(VEHICLE_SPECS.driverZone.h, Math.min(gridY - Math.floor(comp.grid_h / 2), ROWS - comp.grid_h));
      
      onDrop(comp.comp_id, dropX, dropY);
    } catch (err) {
      console.error('Drop error:', err);
    }
  }, [onDrop]);

  // 2D View rendering
  if (viewMode === '2d') {
    return (
      <div 
        ref={containerRef}
        className={`flex-1 relative bg-slate-900 ${isDragOver ? 'ring-2 ring-blue-500 ring-inset' : ''}`} 
        data-testid="2d-canvas-container"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDropEvent}
      >
        <div className="absolute inset-4 border border-white/10 rounded-lg overflow-hidden">
          {/* Grid */}
          <div 
            className="absolute inset-0 grid"
            style={{ 
              gridTemplateColumns: `repeat(${COLS}, 1fr)`,
              gridTemplateRows: `repeat(${ROWS}, 1fr)`,
            }}
          >
            {/* Driver zone */}
            <div 
              className="absolute top-0 left-0 right-0 bg-slate-800/60 flex items-center justify-center"
              style={{ height: `${(VEHICLE_SPECS.driverZone.h / ROWS) * 100}%` }}
            >
              <span className="text-[10px] text-slate-500 uppercase tracking-wider">Driver Area</span>
            </div>
            
            {/* Grid lines */}
            {Array.from({ length: COLS + 1 }).map((_, i) => (
              <div 
                key={`v${i}`} 
                className="absolute top-0 bottom-0 w-px bg-white/5"
                style={{ left: `${(i / COLS) * 100}%` }}
              />
            ))}
            {Array.from({ length: ROWS + 1 }).map((_, i) => (
              <div 
                key={`h${i}`} 
                className="absolute left-0 right-0 h-px bg-white/5"
                style={{ top: `${(i / ROWS) * 100}%` }}
              />
            ))}
            
            {/* Valid placements for move mode (green zones) */}
            {moveMode && validPlacements && moveComp && validPlacements.map((vp, i) => (
              <div
                key={`valid-${i}`}
                className="absolute bg-green-500/30 border-2 border-green-500/60 rounded cursor-pointer hover:bg-green-500/50 transition-colors z-20"
                style={{
                  left: `${(vp.x / COLS) * 100}%`,
                  top: `${(vp.y / ROWS) * 100}%`,
                  width: `${(moveComp.grid_w / COLS) * 100}%`,
                  height: `${(moveComp.grid_h / ROWS) * 100}%`,
                }}
                onClick={() => {
                  onMove(moveMode, vp.x, vp.y);
                  setMoveMode(null);
                  setSelectedInstance(null);
                }}
                data-testid={`valid-placement-${i}`}
              />
            ))}
            
            {/* Placed components */}
            {placedComponents.map(pc => {
              const comp = components.find(c => c.comp_id === pc.comp_id);
              if (!comp) return null;
              const colors = CATEGORY_COLORS[comp.category] || CATEGORY_COLORS.Storage;
              const isSelected = selectedInstance === pc.instance_id;
              const isBeingMoved = moveMode === pc.instance_id;
              
              return (
                <div
                  key={pc.instance_id}
                  className={`absolute rounded-md border-2 flex items-center justify-center cursor-pointer transition-all z-10 hover:z-20 hover:ring-2 hover:ring-blue-400/50 ${
                    isSelected ? 'ring-2 ring-blue-500 z-20' : ''
                  } ${isBeingMoved ? 'opacity-50' : ''}`}
                  style={{
                    left: `${(pc.x / COLS) * 100}%`,
                    top: `${(pc.y / ROWS) * 100}%`,
                    width: `${(comp.grid_w / COLS) * 100}%`,
                    height: `${(comp.grid_h / ROWS) * 100}%`,
                    backgroundColor: colors.fill + '40',
                    borderColor: colors.fill,
                    transform: pc.rotation ? `rotate(${pc.rotation}deg)` : undefined,
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!moveMode) setSelectedInstance(pc.instance_id);
                  }}
                  data-testid={`2d-component-${pc.instance_id}`}
                >
                  <span className="text-[8px] text-white font-medium text-center leading-tight px-1 truncate">
                    {comp.name}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Drop indicator */}
        {isDragOver && (
          <div className="absolute inset-0 flex items-center justify-center bg-blue-500/10 pointer-events-none">
            <div className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium">
              Drop to add component
            </div>
          </div>
        )}
        
        <div className="absolute bottom-3 left-3 text-[10px] text-slate-500 font-jetbrains pointer-events-none select-none">
          <span className="bg-slate-900/70 px-2 py-1 rounded">2D Top View</span>
        </div>
      </div>
    );
  }

  // 3D View rendering
  return (
    <div 
      ref={containerRef}
      className={`flex-1 relative ${isDragOver ? 'ring-2 ring-blue-500 ring-inset' : ''}`} 
      data-testid="three-canvas-container"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDropEvent}
    >
      <Canvas
        camera={{ position: [0, 2.5, 2.5], fov: 45 }}
        shadows
        gl={{ antialias: true, alpha: false, powerPreference: 'default' }}
        dpr={[1, 1.5]}
        style={{ background: '#050a18' }}
        data-testid="three-canvas"
      >
        <color attach="background" args={['#050a18']} />
        <ambientLight intensity={0.5} />
        <directionalLight position={[5, 8, 5]} intensity={0.8} castShadow />
        <directionalLight position={[-3, 5, -3]} intensity={0.3} />
        <pointLight position={[0, 3, 0]} intensity={0.2} color="#3b82f6" />

        <VehicleFloor />

        <GridClickPlane
          onGridClick={handleGridClick}
          validPlacements={moveMode ? validPlacements : null}
          moveComp={moveComp}
        />

        <Suspense fallback={null}>
          {placedComponents.map(pc => {
            const comp = components.find(c => c.comp_id === pc.comp_id);
            if (!comp) return null;
            return (
              <PlacedComponent3D
                key={pc.instance_id}
                pc={pc}
                comp={comp}
                isSelected={selectedInstance === pc.instance_id}
                isHovered={hoveredInstance === pc.instance_id}
                onSelect={setSelectedInstance}
                onHover={setHoveredInstance}
                customMaterials={customMaterials}
              />
            );
          })}
        </Suspense>

        <OrbitControls
          makeDefault
          minPolarAngle={0.2}
          maxPolarAngle={Math.PI / 2.2}
          minDistance={0.5}
          maxDistance={6}
          enablePan
          target={[0, 0, 0]}
        />
      </Canvas>

      <div className="absolute bottom-3 left-3 flex gap-2 text-[10px] text-slate-500 font-jetbrains pointer-events-none select-none">
        <span className="bg-slate-900/70 px-2 py-1 rounded">Orbit: Left drag</span>
        <span className="bg-slate-900/70 px-2 py-1 rounded">Pan: Right drag</span>
        <span className="bg-slate-900/70 px-2 py-1 rounded">Zoom: Scroll</span>
      </div>
    </div>
  );
}
